/**
 * AssetFormModal.jsx
 * New Asset creation form + Edit Asset form
 * Opens when user clicks "+ New Asset" or "Edit Asset" in AssetProfileModal
 *
 * Props:
 *   assetId   — if provided, loads existing asset for editing
 *   onClose   — fn()
 *   onSaved   — fn(newAssetId) — called after successful save
 */
import { useState, useEffect } from 'react';
import { assetsAPI, mastersAPI, vendorsAPI } from '../../api';
import toast from 'react-hot-toast';

/* ── design tokens ─────────────────────────────────────────── */
const C = {
  accent:'#1a56db', ink:'#1a1f2e', soft:'#64748b',
  border:'#e2e8f0', surface:'#f8fafc',
  red:'#dc2626',
};

/* ── field components ──────────────────────────────────────── */
function Row({ label, children, required, span=1, hint }) {
  return (
    <div style={{ gridColumn:`span ${span}` }}>
      <label style={{ display:'block', fontSize:12, fontWeight:600,
        color:'#374151', marginBottom:4 }}>
        {label}{required && <span style={{ color:C.red }}> *</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>{hint}</div>}
    </div>
  );
}

const inputSx = {
  width:'100%', border:`1px solid ${C.border}`, borderRadius:8,
  padding:'8px 11px', fontSize:13, color:C.ink, outline:'none',
  boxSizing:'border-box', background:'#fff', fontFamily:'inherit',
};

function Inp({ label, name, value, onChange, type='text',
  placeholder, required, span=1, hint }) {
  return (
    <Row label={label} required={required} span={span} hint={hint}>
      <input type={type} name={name} value={value??''} onChange={onChange}
        placeholder={placeholder} required={required} style={inputSx} />
    </Row>
  );
}

function Sel({ label, name, value, onChange, options=[],
  required, span=1, hint }) {
  return (
    <Row label={label} required={required} span={span} hint={hint}>
      <select name={name} value={value??''} onChange={onChange}
        style={inputSx}>
        <option value="">Select…</option>
        {options.map(o => (
          <option key={o.value ?? o} value={o.value ?? o}>
            {o.label ?? o}
          </option>
        ))}
      </select>
    </Row>
  );
}

function Textarea({ label, name, value, onChange, rows=3, span=2 }) {
  return (
    <Row label={label} span={span}>
      <textarea name={name} value={value??''} onChange={onChange}
        rows={rows} style={{ ...inputSx, resize:'vertical' }} />
    </Row>
  );
}

function SecHead({ children }) {
  return (
    <div style={{ gridColumn:'span 2', paddingTop:14,
      marginTop:4, borderTop:`1px solid ${C.border}` }}>
      <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8',
        textTransform:'uppercase', letterSpacing:'.08em' }}>{children}</div>
    </div>
  );
}

/* ── tab nav inside modal ──────────────────────────────────── */
const FORM_TABS = [
  { id:'basic',    label:'Basic Info' },
  { id:'purchase', label:'Purchase & Supplier' },
  { id:'warranty', label:'Warranty & Insurance' },
  { id:'finance',  label:'Finance & Depreciation' },
];

/* ════════════════════════════════════════════════════════════ */
export default function AssetFormModal({ assetId, onClose, onSaved }) {
  const isEdit = !!assetId;
  const [tab,    setTab]    = useState('basic');
  const [saving, setSaving] = useState(false);

  /* masters dropdowns */
  const [categories, setCategories] = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [locations,  setLocations]  = useState([]);
  const [departments,setDepartments]= useState([]);
  const [vendors,    setVendors]    = useState([]);
  const [depTypes,   setDepTypes]   = useState([]);
  const [depMethods, setDepMethods] = useState([]);

  const [form, setForm] = useState({
    /* Basic */
    name:'', asset_code:'', category_id:'', serial_number:'',
    make:'', model:'', description:'', location_id:'',
    department:'', assigned_to:'', status:'operational',
    plant_status:'', finance_status:'Active', is_critical:false,
    /* Purchase */
    vendor_id:'', purchase_date:'', purchase_cost:'',
    our_invoice_no:'', our_invoice_date:'',
    supp_invoice_no:'', supp_invoice_date:'',
    installation_date:'', commissioning_date:'',
    po_number:'', invoice_number:'', gst_no:'', gst_amount:'',
    gst_percent:'', payment_terms:'', payment_status:'',
    capex_code:'', approved_by:'',
    subsequent_cost:'', transport_cost:'', installation_cost:'',
    /* Warranty & Insurance */
    warranty_start:'', warranty_end:'',
    has_insurance:'N', insurance_from:'', insurance_to:'',
    has_amc:'N', amc_start_date:'', amc_end_date:'',
    /* Finance */
    dep_type_id:'', useful_life_years:'', residual_value:'',
    depreciation_method:'',
  });

  const set = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  /* load masters */
  useEffect(() => {
    mastersAPI.listCategories().then(r => setCategories(r.data||[])).catch(()=>{});
    mastersAPI.listEmployees().then(r => setEmployees(r.data||[])).catch(()=>{});
    mastersAPI.listDepTypes().then(r => setDepTypes(r.data||[])).catch(()=>{});
    mastersAPI.listDepMethods().then(r => setDepMethods(r.data||[])).catch(()=>{});
    vendorsAPI.list({ limit:200 }).then(r => setVendors(r.data?.data||[])).catch(()=>{});
    /* locations + departments from company master API */
    fetch('/api/company/locations', {
      headers:{ Authorization:`Bearer ${localStorage.getItem('iassets_token')}` }
    }).then(r=>r.json()).then(d=>setLocations(d||[])).catch(()=>{});
    fetch('/api/company/departments', {
      headers:{ Authorization:`Bearer ${localStorage.getItem('iassets_token')}` }
    }).then(r=>r.json()).then(d=>setDepartments(d||[])).catch(()=>{});
  }, []);

  /* if editing, preload asset data */
  useEffect(() => {
    if (!isEdit) return;
    assetsAPI.profile(assetId).then(r => {
      const p = r.data;
      setForm(f => ({
        ...f,
        name: p.name||'', asset_code: p.asset_code||'',
        category_id: p.category_id||'', serial_number: p.serial_number||'',
        make: p.make||'', model: p.model||'', description: p.description||'',
        location_id: p.location_id||'', department: p.department||'',
        assigned_to: p.assigned_to||'', status: p.status||'operational',
        plant_status: p.plant_status||'', finance_status: p.finance_status||'Active',
        is_critical: p.is_critical||false,
        vendor_id: p.vendor_id||'',
        purchase_date: p.purchase_date ? String(p.purchase_date).slice(0,10) : '',
        purchase_cost: p.purchase_cost||'',
        our_invoice_no: p.our_invoice_no||'',
        our_invoice_date: p.our_invoice_date ? String(p.our_invoice_date).slice(0,10):'',
        supp_invoice_no: p.supp_invoice_no||'',
        supp_invoice_date: p.supp_invoice_date ? String(p.supp_invoice_date).slice(0,10):'',
        installation_date: p.installation_date ? String(p.installation_date).slice(0,10):'',
        commissioning_date: p.commissioning_date ? String(p.commissioning_date).slice(0,10):'',
        po_number: p.po_number||'', invoice_number: p.invoice_number||'',
        gst_no: p.gst_no||'', gst_amount: p.gst_amount||'',
        gst_percent: p.gst_percent||'',
        payment_terms: p.payment_terms||'', payment_status: p.payment_status||'',
        capex_code: p.capex_code||'', approved_by: p.approved_by||'',
        subsequent_cost: p.subsequent_cost||'',
        transport_cost: p.transport_cost||'',
        installation_cost: p.installation_cost||'',
        warranty_start: p.warranty_start ? String(p.warranty_start).slice(0,10):'',
        warranty_end:   p.warranty_end   ? String(p.warranty_end).slice(0,10):'',
        has_insurance: p.has_insurance ? 'Y':'N',
        insurance_from: p.insurance_from ? String(p.insurance_from).slice(0,10):'',
        insurance_to:   p.insurance_to   ? String(p.insurance_to).slice(0,10):'',
        has_amc: p.has_amc ? 'Y':'N',
        amc_start_date: p.amc_start_date ? String(p.amc_start_date).slice(0,10):'',
        amc_end_date:   p.amc_end_date   ? String(p.amc_end_date).slice(0,10):'',
        dep_type_id: p.dep_type_id||'',
        useful_life_years: p.useful_life_years||'',
        residual_value: p.residual_value||'',
        depreciation_method: p.depreciation_method||'',
      }));
    }).catch(()=>{});
  }, [assetId]);

  const submit = async e => {
    e.preventDefault();
    if (!form.name.trim())       return toast.error('Asset name is required');
    if (!form.asset_code.trim()) return toast.error('Asset code is required');

    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v]) => {
        if (v !== '' && v !== null && v !== undefined) fd.append(k, v);
      });

      let savedId = assetId;
      if (isEdit) {
        await assetsAPI.update(assetId, fd);
        toast.success('Asset updated!');
      } else {
        const r = await assetsAPI.create(fd);
        savedId = r.data?.id;
        toast.success('Asset created!');
      }
      onSaved(savedId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  /* ── tab content ─────────────────────────────────────────── */
  const renderTab = () => {
    if (tab === 'basic') return (
      <>
        <SecHead>Identification</SecHead>
        <Inp label="Asset Name" name="name" value={form.name}
          onChange={set} required placeholder="Conveyor Belt CB-500" />
        <Inp label="Asset Code" name="asset_code" value={form.asset_code}
          onChange={set} required placeholder="AST-CNV-001"
          hint={isEdit ? 'Code cannot be changed after creation' : ''} span={1} />
        <Sel label="Category" name="category_id" value={form.category_id}
          onChange={set}
          options={categories.map(c=>({value:c.id, label:`${c.code} — ${c.name}`}))} span={1} />
        <Inp label="Make / Brand" name="make" value={form.make}
          onChange={set} placeholder="Fenner" span={1} />
        <Inp label="Model" name="model" value={form.model}
          onChange={set} placeholder="CB-500" span={1} />
        <Inp label="Serial Number" name="serial_number" value={form.serial_number}
          onChange={set} placeholder="SN-2024-XXXX" span={1} />
        <Textarea label="Description" name="description" value={form.description}
          onChange={set} />

        <SecHead>Location & Assignment</SecHead>
        <Sel label="Location" name="location_id" value={form.location_id}
          onChange={set}
          options={locations.map(l=>({value:l.id, label:l.location_name}))} span={1} />
        <Sel label="Department" name="department" value={form.department}
          onChange={set}
          options={departments.map(d=>({value:d.name||d.code, label:d.name}))} span={1} />
        <Sel label="Assigned To (Employee)" name="assigned_to" value={form.assigned_to}
          onChange={set}
          options={employees.map(e=>({value:e.id, label:`${e.emp_code} — ${e.name}`}))} span={2} />

        <SecHead>Status</SecHead>
        <Sel label="Asset Status" name="status" value={form.status}
          onChange={set} required span={1}
          options={['operational','maintenance','breakdown','retired','disposed']
            .map(s=>({value:s, label:s.charAt(0).toUpperCase()+s.slice(1)}))} />
        <Sel label="Plant Status" name="plant_status" value={form.plant_status}
          onChange={set} span={1}
          options={['In Service','Under Repair','Idle','Scrapped','Transferred']
            .map(s=>({value:s,label:s}))} />
        <Sel label="Finance Status" name="finance_status" value={form.finance_status}
          onChange={set} span={1}
          options={[{value:'Active',label:'Active'},{value:'Retired',label:'Retired'}]} />
        <Row label="Critical Asset" span={1}>
          <label style={{ display:'flex', alignItems:'center', gap:8,
            fontSize:13, cursor:'pointer', marginTop:4 }}>
            <input type="checkbox" name="is_critical" checked={!!form.is_critical}
              onChange={set} style={{ width:15, height:15 }} />
            Mark this asset as critical
          </label>
        </Row>
      </>
    );

    if (tab === 'purchase') return (
      <>
        <SecHead>Supplier</SecHead>
        <Sel label="Vendor / Supplier" name="vendor_id" value={form.vendor_id}
          onChange={set} span={2}
          options={vendors.map(v=>({value:v.id, label:`${v.vendor_code} — ${v.name}`}))} />
        <Inp label="PO Number" name="po_number" value={form.po_number}
          onChange={set} placeholder="PO-2024-001" span={1} />
        <Inp label="CAPEX Code" name="capex_code" value={form.capex_code}
          onChange={set} placeholder="CAP-2024-XX" span={1} />
        <Inp label="Approved By" name="approved_by" value={form.approved_by}
          onChange={set} placeholder="Manager name" span={1} />

        <SecHead>Our Invoice</SecHead>
        <Inp label="Our Invoice No" name="our_invoice_no" value={form.our_invoice_no}
          onChange={set} placeholder="INV-2024-001" span={1} />
        <Inp label="Our Invoice Date" name="our_invoice_date" type="date"
          value={form.our_invoice_date} onChange={set} span={1} />

        <SecHead>Supplier Invoice</SecHead>
        <Inp label="Supplier Invoice No" name="supp_invoice_no" value={form.supp_invoice_no}
          onChange={set} placeholder="SINV-2024-001" span={1} />
        <Inp label="Supplier Invoice Date" name="supp_invoice_date" type="date"
          value={form.supp_invoice_date} onChange={set} span={1} />

        <SecHead>Dates</SecHead>
        <Inp label="Purchase Date" name="purchase_date" type="date"
          value={form.purchase_date} onChange={set} span={1} />
        <Inp label="Installation Date" name="installation_date" type="date"
          value={form.installation_date} onChange={set} span={1} />
        <Inp label="Commissioning Date" name="commissioning_date" type="date"
          value={form.commissioning_date} onChange={set} span={1} />

        <SecHead>Cost</SecHead>
        <Inp label="Purchase Cost (₹)" name="purchase_cost" type="number"
          value={form.purchase_cost} onChange={set} placeholder="0.00" span={1} />
        <Inp label="Subsequent Cost (₹)" name="subsequent_cost" type="number"
          value={form.subsequent_cost} onChange={set} placeholder="0.00" span={1} />
        <Inp label="Transport Cost (₹)" name="transport_cost" type="number"
          value={form.transport_cost} onChange={set} placeholder="0.00" span={1} />
        <Inp label="Installation Cost (₹)" name="installation_cost" type="number"
          value={form.installation_cost} onChange={set} placeholder="0.00" span={1} />

        <SecHead>Tax & Payment</SecHead>
        <Inp label="GST Number" name="gst_no" value={form.gst_no}
          onChange={set} placeholder="27AAECM4162K1Z5" span={1} />
        <Inp label="GST %" name="gst_percent" type="number"
          value={form.gst_percent} onChange={set} placeholder="18" span={1} />
        <Inp label="GST Amount (₹)" name="gst_amount" type="number"
          value={form.gst_amount} onChange={set} placeholder="0.00" span={1} />
        <Sel label="Payment Status" name="payment_status" value={form.payment_status}
          onChange={set} span={1}
          options={['Paid','Partial','Pending','On Credit'].map(s=>({value:s,label:s}))} />
        <Inp label="Payment Terms" name="payment_terms" value={form.payment_terms}
          onChange={set} placeholder="Net 30" span={2} />
      </>
    );

    if (tab === 'warranty') return (
      <>
        <SecHead>Warranty</SecHead>
        <Inp label="Warranty Start Date" name="warranty_start" type="date"
          value={form.warranty_start} onChange={set} span={1} />
        <Inp label="Warranty End Date" name="warranty_end" type="date"
          value={form.warranty_end} onChange={set} span={1} />

        <SecHead>Insurance</SecHead>
        <Sel label="Valid Insurance" name="has_insurance" value={form.has_insurance}
          onChange={set} span={1}
          options={[{value:'Y',label:'Yes'},{value:'N',label:'No'}]} />
        {form.has_insurance === 'Y' && (<>
          <div style={{ gridColumn:'span 1' }} />
          <Inp label="Insurance From Date" name="insurance_from" type="date"
            value={form.insurance_from} onChange={set} span={1} />
          <Inp label="Insurance To Date" name="insurance_to" type="date"
            value={form.insurance_to} onChange={set} span={1} />
        </>)}

        <SecHead>AMC (Annual Maintenance Contract)</SecHead>
        <Sel label="Valid AMC" name="has_amc" value={form.has_amc}
          onChange={set} span={1}
          options={[{value:'Y',label:'Yes'},{value:'N',label:'No'}]} />
        {form.has_amc === 'Y' && (<>
          <div style={{ gridColumn:'span 1' }} />
          <Inp label="AMC From Date" name="amc_start_date" type="date"
            value={form.amc_start_date} onChange={set} span={1} />
          <Inp label="AMC To Date" name="amc_end_date" type="date"
            value={form.amc_end_date} onChange={set} span={1} />
        </>)}
      </>
    );

    if (tab === 'finance') return (
      <>
        <SecHead>Depreciation</SecHead>
        <Sel label="Depreciation Type" name="dep_type_id" value={form.dep_type_id}
          onChange={set} span={2}
          options={depTypes.map(d=>({
            value:d.id,
            label:`${d.code} — ${d.description} (${d.method_code})`
          }))} />
        <Sel label="Depreciation Method" name="depreciation_method"
          value={form.depreciation_method} onChange={set} span={1}
          options={depMethods.map(m=>({value:m.code, label:`${m.code} — ${m.description}`}))} />
        <Inp label="Useful Life (Years)" name="useful_life_years" type="number"
          value={form.useful_life_years} onChange={set} placeholder="10" span={1} />
        <Inp label="Residual Value (₹)" name="residual_value" type="number"
          value={form.residual_value} onChange={set} placeholder="0.00" span={1} />

        {/* cost summary preview */}
        {(() => {
          const cost  = parseFloat(form.purchase_cost)    || 0;
          const sub   = parseFloat(form.subsequent_cost)  || 0;
          const trans = parseFloat(form.transport_cost)   || 0;
          const inst  = parseFloat(form.installation_cost)|| 0;
          const total = cost + sub + trans + inst;
          const life  = parseFloat(form.useful_life_years)|| 0;
          const resid = parseFloat(form.residual_value)   || 0;
          const annDep = life > 0 ? (total - resid) / life : 0;
          const fmtR = n => `₹${Number(n.toFixed(2)).toLocaleString('en-IN')}`;
          if (!total) return null;
          return (
            <div style={{ gridColumn:'span 2', marginTop:8,
              border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden' }}>
              <div style={{ padding:'10px 16px', background:C.surface,
                fontSize:11, fontWeight:700, color:'#94a3b8',
                textTransform:'uppercase', letterSpacing:'.06em' }}>
                Cost Summary Preview
              </div>
              {[
                ['Acquisition / Purchase Cost',       fmtR(cost)],
                ['Subsequent Costs',                  fmtR(sub)],
                ['Transport Cost',                    fmtR(trans)],
                ['Installation Cost',                 fmtR(inst)],
                ['Total Capitalised Value',            fmtR(total), true],
                ['Annual Depreciation (SL estimate)', fmtR(annDep)],
              ].map(([label, val, bold]) => (
                <div key={label} style={{ display:'flex', justifyContent:'space-between',
                  padding:'8px 16px', borderTop:`1px solid #f8fafc`,
                  background: bold ? '#f0f6ff' : '#fff' }}>
                  <span style={{ fontSize:13, color: bold ? C.accent : C.ink,
                    fontWeight: bold ? 700 : 400 }}>{label}</span>
                  <span style={{ fontSize:13, fontWeight: bold ? 800 : 600,
                    color: bold ? C.accent : C.ink }}>{val}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </>
    );
  };

  /* ── render ─────────────────────────────────────────────── */
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,14,26,.55)',
      backdropFilter:'blur(4px)', zIndex:1300, display:'flex',
      alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:16, width:780, maxWidth:'97vw',
        maxHeight:'93vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,.22)', overflow:'hidden' }}>

        {/* header */}
        <div style={{ padding:'16px 24px', borderBottom:`1px solid ${C.border}`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          flexShrink:0 }}>
          <div>
            <div style={{ fontSize:17, fontWeight:800, color:C.ink }}>
              {isEdit ? '✏ Edit Asset' : '⊕ New Asset'}
            </div>
            <div style={{ fontSize:12, color:C.soft, marginTop:2 }}>
              {isEdit ? 'Update asset information' : 'Fill in details to register a new asset'}
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none',
            fontSize:22, cursor:'pointer', color:'#94a3b8' }}>✕</button>
        </div>

        {/* tab bar */}
        <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`,
          background:C.surface, flexShrink:0, padding:'0 24px' }}>
          {FORM_TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding:'10px 18px', fontSize:13, fontWeight: tab===t.id ? 700 : 500,
                color: tab===t.id ? C.accent : C.soft, background:'none', border:'none',
                borderBottom: tab===t.id ? `2px solid ${C.accent}` : '2px solid transparent',
                cursor:'pointer', transition:'all .15s', whiteSpace:'nowrap' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* body */}
        <div style={{ flex:1, overflowY:'auto', padding:'20px 24px' }}>
          <form id="assetform" onSubmit={submit}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              {renderTab()}
            </div>
          </form>
        </div>

        {/* footer */}
        <div style={{ padding:'14px 24px', borderTop:`1px solid ${C.border}`,
          display:'flex', alignItems:'center', justifyContent:'space-between',
          flexShrink:0 }}>
          <div style={{ display:'flex', gap:8 }}>
            {FORM_TABS.map((t, i) => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ width:8, height:8, borderRadius:'50%', border:'none',
                  cursor:'pointer',
                  background: tab===t.id ? C.accent : C.border }} />
            ))}
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose}
              style={{ padding:'8px 20px', borderRadius:8, border:`1px solid ${C.border}`,
                background:'#fff', fontSize:13, cursor:'pointer', color:C.ink }}>
              Cancel
            </button>
            <button form="assetform" type="submit" disabled={saving}
              style={{ padding:'8px 24px', borderRadius:8, border:'none',
                background: saving ? '#93c5fd' : C.accent, color:'#fff',
                fontSize:13, fontWeight:700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : isEdit ? 'Update Asset' : 'Create Asset'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
